import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import DocumentManagement from "@/components/DocumentManagement";
import PaymentManagement from "@/components/PaymentManagement";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Car, Users, TrendingUp, DollarSign, Clock, UserCheck, Plus, Edit,
  XCircle, UserPlus, Ban, CheckCircle, Route, Activity, Wallet,
  Search, ArrowUpRight, ArrowDownLeft, CreditCard, Gift, IndianRupee,
  TicketCheck, Send, MessageSquare, Bell, AlertTriangle, BarChart3, Eye, Zap
} from "lucide-react";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } 
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const statusColors = {
  requested: "bg-amber-100 text-amber-700",
  assigned: "bg-blue-100 text-blue-700",
  accepted: "bg-indigo-100 text-indigo-700",
  started: "bg-emerald-100 text-emerald-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const reportStatusColors = {
  pending: "bg-amber-100 text-amber-700",
  investigating: "bg-blue-100 text-blue-700",
  resolved: "bg-green-100 text-green-700",
  dismissed: "bg-slate-100 text-slate-600",
};

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  
  const [metrics, setMetrics] = useState({});
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [commission, setCommission] = useState(100);
  const [rideFilter, setRideFilter] = useState("all");
  const [reports, setReports] = useState([]);
  const [emergencyAlerts, setEmergencyAlerts] = useState([]);

  // Dialogs
  const [assignDialog, setAssignDialog] = useState({ open: false, rideId: "" });
  const [selectedDriver, setSelectedDriver] = useState("");
  const [matchedDrivers, setMatchedDrivers] = useState([]);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [editRideDialog, setEditRideDialog] = useState({ open: false, ride: null });
  const [editFare, setEditFare] = useState("");
  const [addDriverDialog, setAddDriverDialog] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: "", phone: "", password: "", vehicle_number: "", vehicle_type: "" });
  const [reportDialog, setReportDialog] = useState({ open: false, report: null });
  const [reportStatus, setReportStatus] = useState("");
  const [reportNotes, setReportNotes] = useState("");
  const [addServiceDialog, setAddServiceDialog] = useState(false);
  const [newService, setNewService] = useState({ name: "", slug: "", fare_amount: "" });

  // Wallet management state
  const [walletSearchQuery, setWalletSearchQuery] = useState("");
  const [walletUser, setWalletUser] = useState(null);
  const [walletUserTxns, setWalletUserTxns] = useState([]);
  const [walletAction, setWalletAction] = useState({ open: false, type: "add" });
  const [walletAmount, setWalletAmount] = useState("");
  const [walletDescription, setWalletDescription] = useState("");
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loyaltySettings, setLoyaltySettings] = useState({ cashback_per_ride: 10, min_ride_for_cashback: 0 });

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");
  // Notifications
  const [notifications, setNotifications] = useState([]);
  // Pending Recharges
  const [pendingRecharges, setPendingRecharges] = useState([]);
  // Payment Settings
  const [paymentSettings, setPaymentSettings] = useState({ upi_id: "", account_holder: "", qr_code_url: "", bank_name: "", account_number: "", ifsc_code: "" });
  // Referral Stats
  const [referralStats, setReferralStats] = useState({ top_referrers: [], total_referrals: 0, total_bonus_paid: 0 });

  const headers = token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
  const fetchOpts = token ? { headers } : { headers, credentials: "include" };

  const fetchData = useCallback(async () => {
    try {
      const [mRes, rRes, dRes, uRes, stRes, sRes] = await Promise.all([
        fetch(`${API}/admin/metrics`, fetchOpts),
        fetch(`${API}/admin/rides${rideFilter !== "all" ? `?status=${rideFilter}` : ""}`, fetchOpts),
        fetch(`${API}/admin/drivers`, fetchOpts),
        fetch(`${API}/admin/users`, fetchOpts),
        fetch(`${API}/admin/service-types`, fetchOpts),
        fetch(`${API}/admin/settings`, fetchOpts),
      ]);
      if (mRes.ok) setMetrics(await mRes.json());
      if (rRes.ok) setRides(await rRes.json());
      if (dRes.ok) setDrivers(await dRes.json());
      if (uRes.ok) setUsers(await uRes.json());
      if (stRes.ok) setServiceTypes(await stRes.json());
      if (sRes.ok) setCommission((await sRes.json()).commission_amount);
    } catch (e) { console.error("Operation failed:", e); }
  }, [rideFilter]);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/payments/all?status=pending_verification`, fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setPendingPayments(data.payments || []);
      }
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchLoyaltySettings = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/loyalty-settings`, fetchOpts);
      if (res.ok) setLoyaltySettings(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchTickets = useCallback(async () => {
    try { const r = await safeFetch(`${API}/tickets`, fetchOpts); if (r.ok) setTickets(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try { const r = await safeFetch(`${API}/notifications`, fetchOpts); if (r.ok) setNotifications(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchPendingRecharges = useCallback(async () => {
    try { const r = await safeFetch(`${API}/admin/recharges/pending`, fetchOpts); if (r.ok) setPendingRecharges(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, []);
  const fetchPaymentSettings = useCallback(async () => {
    try { const r = await safeFetch(`${API}/admin/payment-settings`, fetchOpts); if (r.ok) setPaymentSettings(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, []);
  const fetchReferralStats = useCallback(async () => {
    try { const r = await safeFetch(`${API}/admin/referrals`, fetchOpts); if (r.ok) setReferralStats(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/reports`, fetchOpts);
      if (res.ok) setReports(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchEmergencyAlerts = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/emergency-alerts`, fetchOpts);
      if (res.ok) setEmergencyAlerts(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (activeTab === "wallet") {
      fetchPendingPayments();
      fetchLoyaltySettings();
      fetchPendingRecharges();
      fetchPaymentSettings();
      fetchReferralStats();
    }
    if (activeTab === "tickets") fetchTickets();
    if (activeTab === "notifications") { fetchNotifications(); fetch(`${API}/notifications/read-all`, { method: "PUT", ...fetchOpts }); }
    if (activeTab === "reports") { fetchReports(); fetchEmergencyAlerts(); }
  }, [activeTab, fetchPendingPayments, fetchLoyaltySettings, fetchReports, fetchEmergencyAlerts]);

  // Fetch matched drivers for smart assignment
  const fetchMatchedDrivers = async (rideId) => {
    setLoadingMatch(true);
    try {
      const res = await safeFetch(`${API}/admin/rides/${rideId}/match-drivers`, fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setMatchedDrivers(data.matched_drivers || []);
      }
    } catch { setMatchedDrivers([]); }
    setLoadingMatch(false);
  };

  const openAssignDialog = (rideId) => {
    setAssignDialog({ open: true, rideId });
    setSelectedDriver("");
    setMatchedDrivers([]);
    fetchMatchedDrivers(rideId);
  };

  const handleAssign = async () => {
    if (!selectedDriver) { toast.error("Select a driver"); return; }
    try {
      const res = await safeFetch(`${API}/admin/rides/${assignDialog.rideId}/assign`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ driver_id: selectedDriver }),
      });
      if (res.ok) { toast.success("Driver assigned"); setAssignDialog({ open: false, rideId: "" }); setMatchedDrivers([]); fetchData(); }
      else toast.error((await res.json()).detail);
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleAutoAssign = async () => {
    try {
      const res = await safeFetch(`${API}/admin/rides/${assignDialog.rideId}/auto-assign`, { method: "POST", ...fetchOpts });
      if (res.ok) { 
        const data = await res.json();
        toast.success(`Auto-assigned to ${data.driver.name} (Score: ${data.score.total_score})`); 
        setAssignDialog({ open: false, rideId: "" }); 
        setMatchedDrivers([]); 
        fetchData(); 
      } else toast.error((await res.json()).detail);
    } catch (e) { console.error(e); toast.error("Auto-assign failed"); }
  };

  const handleEditRide = async () => {
    if (!editRideDialog.ride) return;
    try {
      const res = await safeFetch(`${API}/admin/rides/${editRideDialog.ride.ride_id}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ fare_amount: parseFloat(editFare) }),
      });
      if (res.ok) { toast.success("Ride updated"); setEditRideDialog({ open: false, ride: null }); fetchData(); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleCancelRide = async (rideId) => {
    try {
      const res = await safeFetch(`${API}/admin/rides/${rideId}`, { method: "DELETE", ...fetchOpts });
      if (res.ok) { toast.success("Ride cancelled"); fetchData(); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleAddDriver = async (e) => {
    e.preventDefault();
    try {
      const res = await safeFetch(`${API}/admin/drivers`, { method: "POST", ...fetchOpts, body: JSON.stringify(newDriver) });
      if (res.ok) { toast.success("Driver added"); setAddDriverDialog(false); setNewDriver({ name: "", phone: "", password: "", vehicle_number: "", vehicle_type: "" }); fetchData(); }
      else toast.error((await res.json()).detail);
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleSuspendUser = async (userId) => {
    try {
      const res = await safeFetch(`${API}/admin/users/${userId}/suspend`, { method: "PUT", ...fetchOpts });
      if (res.ok) { toast.success("User status updated"); fetchData(); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleUpdateReport = async () => {
    if (!reportDialog.report) return;
    try {
      const res = await safeFetch(`${API}/admin/reports/${reportDialog.report.report_id}`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ status: reportStatus, admin_notes: reportNotes }),
      });
      if (res.ok) {
        toast.success("Report updated");
        setReportDialog({ open: false, report: null });
        fetchReports();
      }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleResolveEmergency = async (alertId) => {
    try {
      const res = await safeFetch(`${API}/admin/emergency-alerts/${alertId}`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ status: "resolved" }),
      });
      if (res.ok) {
        toast.success("Alert resolved");
        fetchEmergencyAlerts();
      }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      const res = await safeFetch(`${API}/admin/service-types`, {
        method: "POST", ...fetchOpts,
        body: JSON.stringify({ ...newService, fare_amount: parseFloat(newService.fare_amount) }),
      });
      if (res.ok) { toast.success("Service added"); setAddServiceDialog(false); setNewService({ name: "", slug: "", fare_amount: "" }); fetchData(); }
      else toast.error((await res.json()).detail);
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleDeleteService = async (slug) => {
    try {
      const res = await safeFetch(`${API}/admin/service-types/${slug}`, { method: "DELETE", ...fetchOpts });
      if (res.ok) { toast.success("Service removed"); fetchData(); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleUpdateCommission = async () => {
    try {
      const res = await safeFetch(`${API}/admin/settings/commission`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ amount: parseFloat(commission) }),
      });
      if (res.ok) toast.success("Commission updated");
    } catch (e) { console.error("Operation failed:", e); }
  };

  // Wallet management handlers
  const handleSearchUser = async () => {
    if (!walletSearchQuery.trim()) return;
    const found = users.find(u =>
      u.phone?.includes(walletSearchQuery) ||
      u.name?.toLowerCase().includes(walletSearchQuery.toLowerCase()) ||
      u.user_id === walletSearchQuery
    );
    if (!found) { toast.error("User not found"); return; }
    try {
      const res = await safeFetch(`${API}/admin/wallet/${found.user_id}`, fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setWalletUser(data.user);
        setWalletUserTxns(data.transactions);
      } else toast.error("Failed to load wallet");
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleWalletAction = async () => {
    if (!walletUser || !walletAmount || parseFloat(walletAmount) <= 0) {
      toast.error("Enter a valid amount"); return;
    }
    const endpoint = walletAction.type === "add" ? "add" : "deduct";
    try {
      const res = await safeFetch(`${API}/admin/wallet/${walletUser.user_id}/${endpoint}`, {
        method: "POST", ...fetchOpts,
        body: JSON.stringify({ amount: parseFloat(walletAmount), description: walletDescription || `Admin ${endpoint}` }),
      });
      if (res.ok) {
        toast.success(`Balance ${walletAction.type === "add" ? "added" : "deducted"}`);
        setWalletAction({ open: false, type: "add" });
        setWalletAmount(""); setWalletDescription("");
        // Refresh wallet data
        const res2 = await safeFetch(`${API}/admin/wallet/${walletUser.user_id}`, fetchOpts);
        if (res2.ok) {
          const data = await res2.json();
          setWalletUser(data.user);
          setWalletUserTxns(data.transactions);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Action failed");
      }
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleVerifyPayment = async (paymentId, status) => {
    try {
      const res = await safeFetch(`${API}/admin/payments/${paymentId}/verify`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(`Payment ${status}`);
        fetchPendingPayments();
      } else toast.error("Failed to verify payment");
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleUpdateLoyalty = async () => {
    try {
      const res = await safeFetch(`${API}/admin/loyalty-settings`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify(loyaltySettings),
      });
      if (res.ok) toast.success("Loyalty settings updated");
      else toast.error("Failed to update");
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleTicketReply = async () => {
    if (!selectedTicket || !ticketReply.trim()) return;
    try {
      const r = await safeFetch(`${API}/tickets/${selectedTicket.ticket_id}/reply`, { method: "POST", ...fetchOpts, body: JSON.stringify({ message: ticketReply }) });
      if (r.ok) { setTicketReply(""); const tr = await safeFetch(`${API}/tickets/${selectedTicket.ticket_id}`, fetchOpts); if (tr.ok) setSelectedTicket(await tr.json()); fetchTickets(); toast.success("Reply sent"); }
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleCloseTicket = async (ticketId) => {
    try { const r = await safeFetch(`${API}/tickets/${ticketId}/close`, { method: "PUT", ...fetchOpts }); if (r.ok) { toast.success("Ticket closed"); setSelectedTicket(null); fetchTickets(); } } catch (e) { console.error("Operation failed:", e); }
  };

  const handleApproveRecharge = async (txnId) => {
    try { const r = await safeFetch(`${API}/admin/recharges/${txnId}/approve`, { method: "PUT", ...fetchOpts }); if (r.ok) { toast.success("Recharge approved"); fetchPendingRecharges(); } else toast.error((await r.json()).detail); } catch (e) { console.error(e); toast.error("Network error"); }
  };
  const handleRejectRecharge = async (txnId) => {
    try { const r = await safeFetch(`${API}/admin/recharges/${txnId}/reject`, { method: "PUT", ...fetchOpts }); if (r.ok) { toast.success("Recharge rejected"); fetchPendingRecharges(); } } catch (e) { console.error("Operation failed:", e); }
  };
  const handleSavePaymentSettings = async () => {
    try { const r = await safeFetch(`${API}/admin/payment-settings`, { method: "PUT", ...fetchOpts, body: JSON.stringify(paymentSettings) }); if (r.ok) toast.success("Payment settings saved"); else toast.error("Failed"); } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const pendingRides = rides.filter(r => r.ride_status === "requested");

  return (
    <DashboardLayout activeTab={activeTab} onTabChange={setActiveTab}>
      {/* Greeting */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Welcome back, {user?.name?.split(" ")[0]}!
        </h1>
        <p className="text-slate-500 mt-1">Here's what's happening today</p>
      </div>

      {/* Dashboard Overview */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Route className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500">Total Rides</p>
                <p className="font-heading font-bold text-3xl text-slate-900">{metrics.total_rides || 0}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="font-heading font-bold text-3xl text-amber-600">{metrics.pending_rides || 0}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500">Active Drivers</p>
                <p className="font-heading font-bold text-3xl text-emerald-600">{metrics.active_drivers || 0}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500">Revenue</p>
                <p className="font-heading font-bold text-2xl text-slate-900">{"\u20B9"}{metrics.total_revenue || 0}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button onClick={() => setActiveTab("rides")} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
                <Car className="w-4 h-4 mr-2" /> Manage Rides
              </Button>
              <Button onClick={() => setAddDriverDialog(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full">
                <UserPlus className="w-4 h-4 mr-2" /> Add Driver
              </Button>
              <Button onClick={() => setActiveTab("wallet")} variant="outline" className="rounded-full">
                <Wallet className="w-4 h-4 mr-2" /> Wallet & Payments
              </Button>
              <Button onClick={() => setActiveTab("services")} variant="outline" className="rounded-full">
                Service Types
              </Button>
            </CardContent>
          </Card>

          {pendingRides.length > 0 && (
            <Card className="rounded-2xl border-0 shadow-sm bg-amber-50 border-l-4 border-l-amber-500">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-slate-900">{pendingRides.length} rides waiting for assignment</p>
                    <p className="text-sm text-slate-600">Assign drivers to pending rides</p>
                  </div>
                </div>
                <Button onClick={() => { setActiveTab("rides"); setRideFilter("requested"); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full">
                  View Pending
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Rides Tab */}
      {activeTab === "rides" && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {["all", "requested", "assigned", "accepted", "started", "completed", "cancelled"].map((f) => (
              <Button
                key={f}
                data-testid={`ride-filter-${f}`}
                variant={rideFilter === f ? "default" : "outline"}
                onClick={() => setRideFilter(f)}
                className={`rounded-full text-xs capitalize ${rideFilter === f ? "bg-slate-900 text-white" : ""}`}
                size="sm"
              >
                {f}
              </Button>
            ))}
          </div>
          <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-600 text-xs uppercase">ID</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Customer</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Service</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Route</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Fare</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Status</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Driver</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rides.map((ride) => (
                  <TableRow key={ride.ride_id}>
                    <TableCell className="text-xs text-slate-500 font-mono">{ride.ride_id.slice(-8)}</TableCell>
                    <TableCell className="text-sm text-slate-900">
                      {ride.customer_name}
                      {ride.passenger_count > 1 && (
                        <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{ride.passenger_count} pax</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{ride.service_type.replace(/_/g, " ")}</TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <p className="text-slate-700">{ride.pickup_location}</p>
                        <p className="text-slate-400">{ride.drop_location}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{"\u20B9"}{ride.fare_amount}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full text-xs ${statusColors[ride.ride_status]}`}>{ride.ride_status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{ride.driver_name || "-"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {ride.ride_status === "requested" && (
                          <Button size="sm" className="bg-forest text-white rounded-full text-xs h-7 px-3" onClick={() => openAssignDialog(ride.ride_id)}>
                            Assign
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditRideDialog({ open: true, ride }); setEditFare(String(ride.fare_amount)); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!["completed", "cancelled"].includes(ride.ride_status) && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleCancelRide(ride.ride_id)}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rides.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-400">No rides found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Drivers Tab */}
      {activeTab === "drivers" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="font-heading text-xl font-semibold text-slate-900">All Drivers</h2>
            <Button onClick={() => setAddDriverDialog(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add Driver
            </Button>
          </div>
          <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-600 text-xs uppercase">Name</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Phone</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Vehicle</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Status</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Earnings</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Rating</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.map((d) => (
                  <TableRow key={d.driver_id}>
                    <TableCell className="font-medium text-slate-900">{d.name}</TableCell>
                    <TableCell className="text-slate-600">{d.phone}</TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <p className="text-slate-700">{d.vehicle_type}</p>
                        <p className="text-slate-400">{d.vehicle_number}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-full text-xs ${d.status === "online" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {d.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{"\u20B9"}{d.total_earnings}</TableCell>
                    <TableCell className="text-amber-600 font-medium">{d.rating}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">All Users</h2>
          <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-600 text-xs uppercase">Name</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Phone</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Email</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Role</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Wallet</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Status</TableHead>
                  <TableHead className="text-slate-600 text-xs uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                    <TableCell className="text-slate-600">{u.phone || "-"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{u.email || "-"}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full text-xs ${u.role === "admin" ? "bg-slate-100 text-slate-700" : u.role === "driver" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-forest">{"\u20B9"}{u.wallet_balance || 0}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full text-xs ${u.account_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {u.account_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {u.role !== "admin" && (
                          <Button size="sm" variant="ghost" onClick={() => handleSuspendUser(u.user_id)} className={`rounded-full text-xs h-7 px-3 ${u.account_status === "active" ? "text-red-500" : "text-emerald-500"}`}>
                            {u.account_status === "active" ? <><Ban className="w-3 h-3 mr-1" /> Suspend</> : <><CheckCircle className="w-3 h-3 mr-1" /> Activate</>}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Wallet & Payments Tab */}
      {activeTab === "wallet" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Wallet & Payments</h2>

          {/* Pending Payments */}
          {pendingPayments.length > 0 && (
            <Card className="rounded-2xl border-0 shadow-sm border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  Pending Payment Verifications ({pendingPayments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingPayments.map((p) => (
                    <div key={p.payment_id} data-testid={`pending-payment-${p.payment_id}`} className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{p.user_name || "Customer"} - {p.user_phone || ""}</p>
                        <p className="text-xs text-slate-600">Ride: {p.ride_id?.slice(-8)} | {p.payment_method?.toUpperCase()} | {"\u20B9"}{p.amount}</p>
                        {p.upi_reference && <p className="text-xs font-mono font-semibold text-blue-700 mt-1">UTR / Ref: {p.upi_reference}</p>}
                        {p.pickup && <p className="text-xs text-slate-400 mt-0.5">{p.pickup} → {p.drop}</p>}
                        <p className="text-xs text-slate-400">{new Date(p.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`verify-payment-${p.payment_id}`}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs"
                          onClick={() => handleVerifyPayment(p.payment_id, "paid")}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-xs text-red-500 border-red-200"
                          onClick={() => handleVerifyPayment(p.payment_id, "rejected")}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* User Wallet Search */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Manage User Wallet</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="wallet-search-input"
                    value={walletSearchQuery}
                    onChange={(e) => setWalletSearchQuery(e.target.value)}
                    placeholder="Search by name, phone, or user ID"
                    className="pl-10 rounded-xl"
                    onKeyDown={(e) => e.key === "Enter" && handleSearchUser()}
                  />
                </div>
                <Button data-testid="wallet-search-btn" onClick={handleSearchUser} className="bg-slate-900 text-white rounded-xl">
                  Search
                </Button>
              </div>

              {walletUser && (
                <div data-testid="wallet-user-detail" className="bg-slate-50 rounded-xl p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-forest-50 flex items-center justify-center">
                        <span className="font-heading font-bold text-lg text-forest">{walletUser.name?.[0]?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{walletUser.name}</p>
                        <p className="text-xs text-slate-500">{walletUser.phone} | {walletUser.role}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Wallet Balance</p>
                      <p className="font-heading font-bold text-2xl text-forest">{"\u20B9"}{walletUser.wallet_balance || 0}</p>
                      <p className="text-xs text-slate-400">Loyalty: {walletUser.loyalty_points || 0} pts</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      data-testid="admin-wallet-add-btn"
                      onClick={() => setWalletAction({ open: true, type: "add" })}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-full text-sm"
                    >
                      <ArrowDownLeft className="w-4 h-4 mr-1" /> Add Balance
                    </Button>
                    <Button
                      data-testid="admin-wallet-deduct-btn"
                      onClick={() => setWalletAction({ open: true, type: "deduct" })}
                      variant="outline"
                      className="rounded-full text-sm text-red-500 border-red-200"
                    >
                      <ArrowUpRight className="w-4 h-4 mr-1" /> Deduct
                    </Button>
                  </div>

                  {/* Transaction History */}
                  {walletUserTxns.length > 0 && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium text-slate-700 mb-3">Recent Transactions</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {walletUserTxns.map((txn) => (
                          <div key={txn.transaction_id} className="flex items-center justify-between p-2 text-sm">
                            <div>
                              <p className="text-slate-700">{txn.description}</p>
                              <p className="text-xs text-slate-400">{new Date(txn.created_at).toLocaleString()}</p>
                            </div>
                            <span className={`font-medium ${txn.transaction_type === "credit" ? "text-green-600" : "text-red-600"}`}>
                              {txn.transaction_type === "credit" ? "+" : ""}{"\u20B9"}{Math.abs(txn.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loyalty Settings */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-600" />
                Loyalty Program Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 text-sm">Cashback Per Ride ({"\u20B9"})</Label>
                  <Input
                    data-testid="loyalty-cashback-input"
                    type="number"
                    value={loyaltySettings.cashback_per_ride}
                    onChange={(e) => setLoyaltySettings({ ...loyaltySettings, cashback_per_ride: parseFloat(e.target.value) || 0 })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Min Ride Fare for Cashback ({"\u20B9"})</Label>
                  <Input
                    data-testid="loyalty-min-ride-input"
                    type="number"
                    value={loyaltySettings.min_ride_for_cashback}
                    onChange={(e) => setLoyaltySettings({ ...loyaltySettings, min_ride_for_cashback: parseFloat(e.target.value) || 0 })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <Button
                data-testid="save-loyalty-btn"
                onClick={handleUpdateLoyalty}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
              >
                Save Loyalty Settings
              </Button>
            </CardContent>
          </Card>

          {/* Pending Recharge Approvals */}
          {pendingRecharges.length > 0 && (
            <Card className="rounded-2xl border-0 shadow-sm border-l-4 border-l-blue-500">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  Pending Recharge Approvals ({pendingRecharges.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingRecharges.map(r => (
                    <div key={r.transaction_id} data-testid={`pending-recharge-${r.transaction_id}`} className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{r.user_name || "User"} - {"\u20B9"}{r.amount}</p>
                        <p className="text-xs text-slate-500">{r.payment_method?.toUpperCase()} {r.upi_reference ? `| Ref: ${r.upi_reference}` : ""}</p>
                        <p className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button data-testid={`approve-recharge-${r.transaction_id}`} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs" onClick={() => handleApproveRecharge(r.transaction_id)}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-xs text-red-500 border-red-200" onClick={() => handleRejectRecharge(r.transaction_id)}>
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Settings (UPI) */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-forest" />
                Payment Gateway Settings (UPI / Bank)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-slate-700 text-sm">UPI ID</Label><Input data-testid="payment-upi-id" value={paymentSettings.upi_id || ""} onChange={e => setPaymentSettings({ ...paymentSettings, upi_id: e.target.value })} placeholder="yourname@upi" className="mt-1 rounded-xl" /></div>
                <div><Label className="text-slate-700 text-sm">Account Holder Name</Label><Input value={paymentSettings.account_holder || ""} onChange={e => setPaymentSettings({ ...paymentSettings, account_holder: e.target.value })} placeholder="Cab Valley" className="mt-1 rounded-xl" /></div>
                <div><Label className="text-slate-700 text-sm">Bank Name</Label><Input value={paymentSettings.bank_name || ""} onChange={e => setPaymentSettings({ ...paymentSettings, bank_name: e.target.value })} placeholder="e.g., SBI, HDFC" className="mt-1 rounded-xl" /></div>
                <div><Label className="text-slate-700 text-sm">Account Number</Label><Input value={paymentSettings.account_number || ""} onChange={e => setPaymentSettings({ ...paymentSettings, account_number: e.target.value })} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-slate-700 text-sm">IFSC Code</Label><Input value={paymentSettings.ifsc_code || ""} onChange={e => setPaymentSettings({ ...paymentSettings, ifsc_code: e.target.value })} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-slate-700 text-sm">QR Code URL</Label><Input value={paymentSettings.qr_code_url || ""} onChange={e => setPaymentSettings({ ...paymentSettings, qr_code_url: e.target.value })} placeholder="https://..." className="mt-1 rounded-xl" /></div>
              </div>
              <Button data-testid="save-payment-settings-btn" onClick={handleSavePaymentSettings} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl">Save Payment Settings</Button>
            </CardContent>
          </Card>

          {/* Referral Stats */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-600" />
                Referral Program Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-forest-50 rounded-xl"><p className="font-heading font-bold text-3xl text-forest">{referralStats.total_referrals}</p><p className="text-sm text-slate-500">Total Referrals</p></div>
                <div className="text-center p-4 bg-amber-50 rounded-xl"><p className="font-heading font-bold text-3xl text-amber-600">{"\u20B9"}{referralStats.total_bonus_paid}</p><p className="text-sm text-slate-500">Total Bonus Paid</p></div>
              </div>
              {referralStats.top_referrers.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">Top Referrers</p>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs uppercase">Name</TableHead><TableHead className="text-xs uppercase">Code</TableHead><TableHead className="text-xs uppercase">Referrals</TableHead><TableHead className="text-xs uppercase">Earned</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {referralStats.top_referrers.map((r, i) => (
                        <TableRow key={i}><TableCell className="text-sm">{r.name}</TableCell><TableCell className="font-mono text-sm">{r.referral_code}</TableCell><TableCell className="font-bold">{r.count}</TableCell><TableCell className="font-bold text-forest">{"\u20B9"}{r.earnings}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Services Tab - Admin has read-only access (CRUD restricted to Super Admin) */}
      {activeTab === "services" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="font-heading text-xl font-semibold text-slate-900">Service Types</h2>
            <p className="text-xs text-slate-500">View only - Contact Super Admin to modify services</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {serviceTypes.map((st) => (
              <Card key={st.slug} className={`rounded-2xl border-0 shadow-sm ${!st.is_active ? "opacity-50" : ""}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-heading font-semibold text-slate-900">{st.name}</h4>
                    <Badge className={`rounded-full text-xs ${st.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {st.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">{st.slug}</p>
                  <p className="font-heading font-bold text-2xl text-slate-900">{"\u20B9"}{st.fare_amount}</p>
                  {st.description && <p className="text-xs text-slate-500 mt-2">{st.description}</p>}
                  {st.default_eta && <p className="text-xs text-slate-400 mt-1">ETA: {st.default_eta}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === "analytics" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-forest" />
            Analytics Dashboard
          </h2>
          <AnalyticsDashboard token={token} />
        </div>
      )}

      {/* Reports Tab - Misbehavior Reports & Emergency Alerts */}
      {activeTab === "reports" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            Reports & Issues
          </h2>

          {/* Emergency Alerts */}
          {emergencyAlerts.filter(a => a.status === "active").length > 0 && (
            <Card className="rounded-2xl border-2 border-red-300 shadow-sm bg-red-50">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-red-700 flex items-center gap-2">
                  🚨 Active Emergency Alerts ({emergencyAlerts.filter(a => a.status === "active").length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {emergencyAlerts.filter(a => a.status === "active").map((alert) => (
                  <div key={alert.alert_id} className="bg-white rounded-xl p-4 border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-red-800">{alert.user_name} ({alert.user_phone})</p>
                        <p className="text-sm text-red-600">Location: {alert.location}</p>
                        {alert.ride_id && <p className="text-xs text-red-500">Ride: {alert.ride_id}</p>}
                        <p className="text-xs text-red-400 mt-1">{new Date(alert.created_at).toLocaleString()}</p>
                      </div>
                      <Button
                        data-testid={`resolve-alert-${alert.alert_id}`}
                        onClick={() => handleResolveEmergency(alert.alert_id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Resolved
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* User Reports */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">User Reports</CardTitle>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No reports submitted yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Report ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reporter</TableHead>
                      <TableHead>Reported</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report.report_id} data-testid={`report-row-${report.report_id}`}>
                        <TableCell className="font-mono text-xs">{report.report_id.slice(-8)}</TableCell>
                        <TableCell>
                          <Badge className={`${report.report_type === "misbehavior" ? "bg-red-100 text-red-700" : report.report_type === "safety" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-700"} rounded-full text-xs`}>
                            {report.report_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{report.reporter_name} <span className="text-xs text-slate-400">({report.reporter_role})</span></TableCell>
                        <TableCell>{report.reported_user_name}</TableCell>
                        <TableCell><Badge className={`${reportStatusColors[report.status]} rounded-full text-xs`}>{report.status}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-500">{new Date(report.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`view-report-${report.report_id}`}
                            onClick={() => {
                              setReportDialog({ open: true, report });
                              setReportStatus(report.status);
                              setReportNotes(report.admin_notes || "");
                            }}
                            className="rounded-full text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Old Revenue Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Revenue Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-slate-600">Total Revenue</span>
                    <span className="font-heading font-bold text-xl text-slate-900">{"\u20B9"}{metrics.total_revenue || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-slate-600">Commission Earned</span>
                    <span className="font-heading font-bold text-xl text-amber-600">{"\u20B9"}{metrics.total_commission || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-slate-600">Completed Rides</span>
                    <span className="font-heading font-bold text-xl text-emerald-600">{metrics.completed_rides || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Commission Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-700">Commission Per Ride ({"\u20B9"})</Label>
                  <Input type="number" value={commission} onChange={(e) => setCommission(e.target.value)} className="mt-2 rounded-xl" />
                </div>
                <Button onClick={handleUpdateCommission} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl">
                  Save Commission
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Report Details Dialog */}
      <Dialog open={reportDialog.open} onOpenChange={(o) => setReportDialog({ ...reportDialog, open: o })}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Report Details</DialogTitle>
            <DialogDescription>Review and update report status</DialogDescription>
          </DialogHeader>
          {reportDialog.report && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Report ID:</span><p className="font-mono">{reportDialog.report.report_id}</p></div>
                <div><span className="text-slate-500">Ride ID:</span><p className="font-mono">{reportDialog.report.ride_id}</p></div>
                <div><span className="text-slate-500">Reporter:</span><p>{reportDialog.report.reporter_name} ({reportDialog.report.reporter_role})</p></div>
                <div><span className="text-slate-500">Reported:</span><p>{reportDialog.report.reported_user_name}</p></div>
              </div>
              <div>
                <span className="text-slate-500 text-sm">Type:</span>
                <Badge className={`ml-2 ${reportDialog.report.report_type === "misbehavior" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"} rounded-full`}>
                  {reportDialog.report.report_type}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500 text-sm">Description:</span>
                <p className="mt-1 p-3 bg-slate-50 rounded-lg text-sm">{reportDialog.report.description}</p>
              </div>
              <div>
                <Label className="text-slate-700">Status</Label>
                <Select value={reportStatus} onValueChange={setReportStatus}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Admin Notes</Label>
                <Textarea
                  value={reportNotes}
                  onChange={(e) => setReportNotes(e.target.value)}
                  placeholder="Add notes about the investigation..."
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialog({ open: false, report: null })} className="rounded-xl">Cancel</Button>
            <Button onClick={handleUpdateReport} className="bg-forest hover:bg-forest-dark text-white rounded-xl">Update Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Tab */}
      {activeTab === "documents" && (
        <DocumentManagement isSuperAdmin={false} />
      )}

      {/* Payments Tab */}
      {activeTab === "payments" && (
        <PaymentManagement token={token} isSuperAdmin={false} />
      )}

      {activeTab === "tickets" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Support Tickets</h2>
          {selectedTicket ? (
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle className="font-heading text-lg">{selectedTicket.subject}</CardTitle><p className="text-xs text-slate-400 mt-1">#{selectedTicket.ticket_id.slice(-8)} | {selectedTicket.user_name} | {selectedTicket.category}</p></div>
                <div className="flex gap-2">
                  {selectedTicket.status !== "closed" && <Button variant="outline" size="sm" onClick={() => handleCloseTicket(selectedTicket.ticket_id)} className="rounded-full text-xs text-red-500 border-red-200">Close</Button>}
                  <Button variant="outline" size="sm" onClick={() => setSelectedTicket(null)} className="rounded-full text-xs">Back</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {selectedTicket.messages?.map((m, i) => (
                    <div key={i} className={`p-3 rounded-xl ${m.role === "admin" || m.role === "super_admin" ? "bg-blue-50 ml-8" : "bg-slate-50 mr-8"}`}>
                      <div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium text-slate-700">{m.sender_name}</span><Badge className="text-[10px] rounded-full">{m.role}</Badge></div>
                      <p className="text-sm text-slate-700">{m.content}</p>
                      <p className="text-xs text-slate-400 mt-1">{new Date(m.timestamp).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                {selectedTicket.status !== "closed" && (
                  <div className="flex gap-2">
                    <Input value={ticketReply} onChange={e => setTicketReply(e.target.value)} placeholder="Type a reply..." className="rounded-xl" onKeyDown={e => e.key === "Enter" && handleTicketReply()} />
                    <Button onClick={handleTicketReply} className="bg-forest text-white rounded-xl"><Send className="w-4 h-4" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase text-slate-600">ID</TableHead>
                    <TableHead className="text-xs uppercase text-slate-600">User</TableHead>
                    <TableHead className="text-xs uppercase text-slate-600">Subject</TableHead>
                    <TableHead className="text-xs uppercase text-slate-600">Category</TableHead>
                    <TableHead className="text-xs uppercase text-slate-600">Status</TableHead>
                    <TableHead className="text-xs uppercase text-slate-600">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map(t => (
                    <TableRow key={t.ticket_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedTicket(t)}>
                      <TableCell className="text-xs text-slate-500 font-mono">{t.ticket_id.slice(-8)}</TableCell>
                      <TableCell className="text-sm text-slate-900">{t.user_name}</TableCell>
                      <TableCell className="text-sm text-slate-700">{t.subject}</TableCell>
                      <TableCell><Badge className="rounded-full text-xs">{t.category}</Badge></TableCell>
                      <TableCell><Badge className={`rounded-full text-xs ${t.status === "open" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{t.status}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-400">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                  {tickets.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No tickets</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      )}

      {/* Assign Driver Dialog - Smart Matching */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => { setAssignDialog({ ...assignDialog, open: o }); setMatchedDrivers([]); }}>
        <DialogContent className="rounded-2xl max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign Driver</DialogTitle>
            <DialogDescription>Smart matching based on distance, rating & response time</DialogDescription>
          </DialogHeader>
          
          {/* Auto-Assign Button */}
          <div className="flex gap-2 mb-4">
            <Button 
              data-testid="auto-assign-btn"
              onClick={handleAutoAssign} 
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl"
              disabled={loadingMatch || matchedDrivers.length === 0}
            >
              <Zap className="w-4 h-4 mr-2" /> Auto-Assign Best Driver
            </Button>
          </div>

          {/* Matched Drivers List */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {loadingMatch ? (
              <div className="text-center py-8 text-slate-500">Finding best matches...</div>
            ) : matchedDrivers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">No available drivers found</div>
            ) : (
              matchedDrivers.map((d, idx) => (
                <div 
                  key={d.driver_id}
                  data-testid={`matched-driver-${d.driver_id}`}
                  onClick={() => setSelectedDriver(d.driver_id)}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedDriver === d.driver_id 
                      ? "border-forest bg-forest-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? "bg-amber-100 text-amber-700" : 
                        idx === 1 ? "bg-slate-200 text-slate-700" : 
                        idx === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        #{idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{d.name}</p>
                        <p className="text-xs text-slate-500">{d.vehicle_type} - {d.vehicle_number}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-forest">{d.total_score}</p>
                      <p className="text-xs text-slate-500">Match Score</p>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-blue-50 p-2 rounded-lg text-center">
                      <p className="text-blue-700 font-medium">{d.distance_km ? `${d.distance_km}km` : "N/A"}</p>
                      <p className="text-blue-500">Distance</p>
                    </div>
                    <div className="bg-amber-50 p-2 rounded-lg text-center">
                      <p className="text-amber-700 font-medium">{d.average_rating?.toFixed(1) || "N/A"} ★</p>
                      <p className="text-amber-500">Rating</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded-lg text-center">
                      <p className="text-green-700 font-medium">{d.completed_rides || 0}</p>
                      <p className="text-green-500">Rides</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Manual Selection Fallback */}
          <div className="pt-4 border-t mt-4">
            <p className="text-xs text-slate-500 mb-2">Or select manually:</p>
            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
              <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>
                {drivers.filter((d) => d.status === "online").map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name} - {d.vehicle_type}</SelectItem>
                ))}
                {drivers.filter((d) => d.status !== "online").map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name} (offline)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, rideId: "" })} className="rounded-xl">Cancel</Button>
            <Button onClick={handleAssign} disabled={!selectedDriver} className="bg-forest text-white rounded-xl">Assign Selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Ride Dialog */}
      <Dialog open={editRideDialog.open} onOpenChange={(o) => setEditRideDialog({ ...editRideDialog, open: o })}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Ride</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-slate-700">Fare Amount ({"\u20B9"})</Label>
            <Input type="number" value={editFare} onChange={(e) => setEditFare(e.target.value)} className="mt-2 rounded-xl" />
          </div>
          <DialogFooter>
            <Button onClick={handleEditRide} className="bg-forest text-white rounded-xl">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Driver Dialog */}
      <Dialog open={addDriverDialog} onOpenChange={setAddDriverDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Driver</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddDriver} className="space-y-3">
            <div><Label>Name</Label><Input value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} required className="mt-1 rounded-xl" /></div>
            <div><Label>Phone</Label><Input value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} required className="mt-1 rounded-xl" /></div>
            <div><Label>Password</Label><Input type="password" value={newDriver.password} onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })} required className="mt-1 rounded-xl" /></div>
            <div><Label>Vehicle Number</Label><Input value={newDriver.vehicle_number} onChange={(e) => setNewDriver({ ...newDriver, vehicle_number: e.target.value })} required className="mt-1 rounded-xl" /></div>
            <div><Label>Vehicle Type</Label><Input value={newDriver.vehicle_type} onChange={(e) => setNewDriver({ ...newDriver, vehicle_type: e.target.value })} required placeholder="e.g., Sedan, SUV" className="mt-1 rounded-xl" /></div>
            <Button type="submit" className="w-full bg-forest text-white rounded-xl">Add Driver</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={addServiceDialog} onOpenChange={setAddServiceDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Service Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddService} className="space-y-3">
            <div><Label>Name</Label><Input value={newService.name} onChange={(e) => setNewService({ ...newService, name: e.target.value })} required className="mt-1 rounded-xl" /></div>
            <div><Label>Slug</Label><Input value={newService.slug} onChange={(e) => setNewService({ ...newService, slug: e.target.value })} required placeholder="e.g., vip_transfer" className="mt-1 rounded-xl" /></div>
            <div><Label>Fare ({"\u20B9"})</Label><Input type="number" value={newService.fare_amount} onChange={(e) => setNewService({ ...newService, fare_amount: e.target.value })} required className="mt-1 rounded-xl" /></div>
            <Button type="submit" className="w-full bg-forest text-white rounded-xl">Add Service</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Wallet Add/Deduct Dialog */}
      <Dialog open={walletAction.open} onOpenChange={(o) => setWalletAction({ ...walletAction, open: o })}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {walletAction.type === "add" ? "Add Balance" : "Deduct Balance"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700">Amount ({"\u20B9"})</Label>
              <Input
                data-testid="wallet-action-amount"
                type="number"
                value={walletAmount}
                onChange={(e) => setWalletAmount(e.target.value)}
                placeholder="Enter amount"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-slate-700">Description</Label>
              <Input
                data-testid="wallet-action-description"
                value={walletDescription}
                onChange={(e) => setWalletDescription(e.target.value)}
                placeholder={walletAction.type === "add" ? "e.g., Promotional credit" : "e.g., Refund deduction"}
                className="mt-1 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="wallet-action-confirm"
              onClick={handleWalletAction}
              className={`rounded-xl ${walletAction.type === "add" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"} text-white`}
            >
              {walletAction.type === "add" ? "Add Balance" : "Deduct Balance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
